import { useEffect, useState } from 'react';
import tournamentData from './tournamentData.json';
import { CoffeeShop, TournamentConfig } from './types';
import { ROUNDS_META, ResolvedMatch, resolveBracket, getChampionId } from './bracketUtils';
import CoffeeShopModal from './CoffeeShopModal';
import './CoffeeTournament.css';

const data = tournamentData as TournamentConfig;

function findShop(id: string | null, shops: CoffeeShop[]): CoffeeShop | null {
  if (!id) return null;
  return shops.find((s) => s.id === id) ?? null;
}

// Rules support "**bold**" markdown for emphasis on a phrase within a rule.
function renderRuleText(rule: string) {
  return rule.split(/(\*\*[^*]+\*\*)/g).map((segment, i) =>
    segment.startsWith('**') && segment.endsWith('**') ? (
      <strong key={i}>{segment.slice(2, -2)}</strong>
    ) : (
      segment
    )
  );
}

export default function CoffeeTournament() {
  const [selectedShop, setSelectedShop] = useState<CoffeeShop | null>(null);

  useEffect(() => {
    if (!selectedShop) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setSelectedShop(null);
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedShop]);

  const rounds = resolveBracket(data);
  const champion = findShop(getChampionId(data), data.shops);

  return (
    <div className="ct-page">
      <header className="ct-header">
        <div className="ct-header-ring" aria-hidden="true" />
        <div className="ct-header-content">
          <p className="ct-kicker">☕ {data.location}</p>
          <h1>{data.eventName}</h1>
          <p className="ct-tagline">16 shops. 4 rounds. 1 champion.</p>
          <div className="ct-header-actions">
            {data.signUpUrl && (
              <a
                className="ct-signup-btn"
                href={data.signUpUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Sign Up to Vote
              </a>
            )}
            {data.checkInUrl && (
              <a
                className="ct-checkin-btn"
                href={data.checkInUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Check In at a Shop
              </a>
            )}
            {data.quickCheckInUrl && (
              <a
                className="ct-checkin-btn"
                href={data.quickCheckInUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Quick Check-In (Overall Score Only)
              </a>
            )}
          </div>
        </div>
      </header>

      <section className="ct-rules" aria-label="Tournament rules">
        <h2>Rules</h2>
        <ol>
          {data.rules.map((rule, i) => (
            <li key={i}>{renderRuleText(rule)}</li>
          ))}
        </ol>
      </section>

      <section className="ct-criteria" aria-label="Rating criteria">
        <h2>How We're Rating</h2>
        <p className="ct-criteria-sub">Rate each shop from 1–10 in every category:</p>
        <ul className="ct-criteria-grid">
          {data.ratingCriteria.map((criterion, i) => (
            <li className="ct-criteria-item" key={i}>
              {criterion}
            </li>
          ))}
        </ul>
      </section>

      {champion && <div className="ct-champion-banner">🏆 Champion: {champion.name}</div>}

      <p className="ct-scroll-hint">Swipe to see later rounds →</p>

      <section className="ct-bracket-wrap">
        <div className="ct-bracket">
          {rounds.map((round, rIdx) => (
            <div className={`ct-round ct-round--r${rIdx + 1}`} key={rIdx}>
              <div className="ct-round-title">
                <span className="ct-round-title-name">
                  <span className="ct-round-title-dot" />
                  {ROUNDS_META[rIdx].name}
                </span>
                <span className="ct-round-title-roast">{ROUNDS_META[rIdx].roast}</span>
              </div>
              <div className="ct-round-matches">
                {round.map((match) => (
                  <MatchCard
                    key={match.matchId}
                    match={match}
                    shops={data.shops}
                    onSelectShop={setSelectedShop}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {selectedShop && (
        <CoffeeShopModal shop={selectedShop} onClose={() => setSelectedShop(null)} />
      )}
    </div>
  );
}

interface MatchCardProps {
  match: ResolvedMatch;
  shops: CoffeeShop[];
  onSelectShop: (shop: CoffeeShop) => void;
}

function MatchCard({ match, shops, onSelectShop }: MatchCardProps) {
  const shopA = findShop(match.shopAId, shops);
  const shopB = findShop(match.shopBId, shops);
  const winnerId = match.result?.winnerId;

  return (
    <div className="ct-match">
      <ShopSlot
        shop={shopA}
        votes={match.result?.shopAVotes}
        isWinner={!!winnerId && !!shopA && winnerId === shopA.id}
        isLoser={!!winnerId && !!shopA && winnerId !== shopA.id}
        tbdLabel={match.tbdLabels ? match.tbdLabels[0] : 'TBD'}
        onSelect={onSelectShop}
      />
      <ShopSlot
        shop={shopB}
        votes={match.result?.shopBVotes}
        isWinner={!!winnerId && !!shopB && winnerId === shopB.id}
        isLoser={!!winnerId && !!shopB && winnerId !== shopB.id}
        tbdLabel={match.tbdLabels ? match.tbdLabels[1] : 'TBD'}
        onSelect={onSelectShop}
      />
    </div>
  );
}

interface ShopSlotProps {
  shop: CoffeeShop | null;
  votes?: number;
  isWinner: boolean;
  isLoser: boolean;
  tbdLabel: string;
  onSelect: (shop: CoffeeShop) => void;
}

function ShopSlot({ shop, votes, isWinner, isLoser, tbdLabel, onSelect }: ShopSlotProps) {
  if (!shop) {
    return <div className="ct-slot ct-slot-tbd">{tbdLabel}</div>;
  }

  const classNames = ['ct-slot'];
  if (isWinner) classNames.push('ct-slot-winner');
  if (isLoser) classNames.push('ct-slot-loser');

  return (
    <button className={classNames.join(' ')} onClick={() => onSelect(shop)}>
      <span className="ct-slot-name">{shop.name}</span>
      {votes !== undefined && <span className="ct-slot-votes">{votes}</span>}
    </button>
  );
}
